import React, { useCallback, useEffect, useState } from 'react';
import { colorConstants } from 'config/colorConstants';
import { LOGIN_CAROUSEL_INTERVAL_MS, LOGIN_CAROUSEL_SLIDES } from 'constants/loginCarousel';

// Figma: 143.24deg gradient sitting under the photography, visible through its edges.
const panelBackground = `linear-gradient(143.24deg, ${colorConstants.loginV2GradientStart} 22.24%, ${colorConstants.loginV2GradientEnd} 203.99%)`;

// Figma "Rectangle 3257": 386px of scrim over the bottom 992px-tall panel.
const scrimBackground = `linear-gradient(to top, ${colorConstants.black} 0.9%, ${colorConstants.loginV2ScrimMid} 38.5%, ${colorConstants.loginV2ScrimTop} 89.8%)`;

/**
 * Right-hand panel of the V2 login screen: a crossfading photo carousel with a
 * caption and a pager. Hand-rolled rather than antd's `Carousel` so the scrim,
 * caption and pill pager can be positioned exactly as designed.
 */
export const LoginCarousel = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        if (isPaused) return undefined;
        const timer = setInterval(() => {
            setActiveIndex((current) => (current + 1) % LOGIN_CAROUSEL_SLIDES.length);
        }, LOGIN_CAROUSEL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [isPaused]);

    const pause = useCallback(() => setIsPaused(true), []);
    const resume = useCallback(() => setIsPaused(false), []);

    const activeSlide = LOGIN_CAROUSEL_SLIDES[activeIndex];

    return (
        <div
            className="relative h-full w-full overflow-hidden rounded-[24px]"
            style={{ background: panelBackground }}
            onMouseEnter={pause}
            onMouseLeave={resume}
        >
            {LOGIN_CAROUSEL_SLIDES.map((slide, index) => (
                <img
                    key={slide.title}
                    src={slide.image}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
                    style={{ opacity: index === activeIndex ? 1 : 0 }}
                />
            ))}

            <div className="absolute inset-x-0 bottom-0 h-[39%]" style={{ background: scrimBackground }} />

            <div className="absolute inset-x-0 bottom-[88px] flex flex-col items-center gap-3 px-11 text-center">
                <p className="m-0 font-poppins text-[24px] font-bold leading-normal tracking-[0.24px] text-white">
                    {activeSlide.title}
                </p>
                <p className="m-0 max-w-[672px] font-plex text-[20px] font-normal leading-normal tracking-[0.2px] text-white/70">
                    {activeSlide.body}
                </p>
            </div>

            <div
                role="group"
                aria-label="Carousel navigation"
                className="absolute bottom-6 left-1/2 flex h-4 -translate-x-1/2 items-center gap-2 rounded-full bg-white/20 px-2"
            >
                {LOGIN_CAROUSEL_SLIDES.map((slide, index) => {
                    const isActive = index === activeIndex;
                    return (
                        <button
                            key={slide.title}
                            type="button"
                            aria-label={slide.title}
                            aria-current={isActive}
                            onClick={() => setActiveIndex(index)}
                            className={`cursor-pointer rounded-full border-0 p-0 ${isActive ? 'h-2 w-2' : 'h-1 w-1 opacity-30'}`}
                            style={{ backgroundColor: colorConstants.loginV2PagerDot }}
                        />
                    );
                })}
            </div>
        </div>
    );
};
